// Entry for the v1 mobile shell, booted by `?ui=v2` (see src/boot.ts). The
// desktop entry (src/main.ts) stays the default until U7.

import './theme.css';
import { bus } from '../core/events';
import { GameStateStore } from '../core/store';
import { setLocale } from '../i18n';
import { ensurePushRegistered } from '../net/push';
import { fetchLiveSeason } from '../net/seasonConfig';
import { installSeason, shouldAdoptSeason } from '../sim/season';
import { registerServiceWorker } from '../net/serviceWorker';
import { SupabaseClient } from '../net/supabase';
import { GameSession, wireTeachableMoments } from './game';
import { bindBrowserBack, Router } from './router';
import { createShell } from './shell';
import { coopScreen } from './screens/coop';
import { homePickScreen } from './screens/homePick';
import { birdScreen } from './screens/bird';
import { openRunOverSheet } from './screens/runOver';

/** The parts of `window.history` the router uses, behind a testable seam. */
function historyPort(): {
  push(url: string): void;
  replace(url: string): void;
  back(): void;
} {
  return {
    push: (url) => window.history.pushState(null, '', url),
    replace: (url) => window.history.replaceState(null, '', url),
    back: () => window.history.back(),
  };
}

/** How long boot waits for the season before playing on the bundle. */
const SEASON_WAIT_MS = 2_500;

/**
 * Puts the live season in force, if this save should be playing it.
 *
 * Never throws and never blocks past `SEASON_WAIT_MS`: every failure leaves the
 * bundled defaults standing, which is what the game already did before seasons
 * existed. A refused season is not installed — `net/seasonConfig.ts` already
 * declines a config whose hash does not match, and that decision is not
 * revisited here.
 */
async function resolveSeason(store: GameStateStore): Promise<void> {
  const result = await Promise.race([
    fetchLiveSeason(),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), SEASON_WAIT_MS)),
  ]);
  if (!result?.ok) return;

  const { season } = result;
  if (!shouldAdoptSeason(store.getState())) return;

  installSeason({
    id: season.id,
    config: season.config,
    configHash: season.configHash,
    source: season.source,
  });
  // Stamped through the store so persistence and subscribers see it like any
  // other state change; the stamp is what a server replay will check.
  store.mutate((state) => {
    state.season = {
      seasonId: season.id,
      configHash: season.configHash,
      ...(season.configHash ? {} : { unverified: true }),
    };
  });
}

async function boot(): Promise<void> {
  document.documentElement.classList.add('cf-v2');

  const store = new GameStateStore();
  const state = await store.init();
  setLocale(state.settings.locale);

  const shell = createShell();
  // The session and the router point at each other — a bird tap opens a screen,
  // the end of a Veteran run opens a sheet — so the router is declared first and
  // filled in before anything can call either.
  let router: Router<HTMLElement>;
  const game = new GameSession(store, shell, {
    onToast: (message) => shell.toast(message),
    onBirdTap: (chickenId) => router.push(birdScreen(), { chicken: chickenId }),
    onRunOver: () => openRunOverSheet(shell, store),
  });
  router = new Router<HTMLElement>(
    { store, shell, game },
    {
      mount: (entry, transition) => shell.mountScreen(entry, transition),
      unmount: () => shell.unmountScreen(),
    },
    historyPort(),
  );
  bindBrowserBack(router);

  // The old shell's reduced-motion switch, so the v2 screens obey the same
  // setting while both UIs are still in the build.
  const syncReducedMotion = (): void => {
    document.documentElement.classList.toggle(
      'cf-reduced-motion',
      store.getState().settings.reducedMotion,
    );
  };
  syncReducedMotion();
  store.subscribe(syncReducedMotion);
  wireTeachableMoments(store, (message) => shell.toast(message));

  game.start();

  // A handle for the U8 screenshot harness, in dev builds only: the harness has
  // to open a screen the game would not offer on demand (a broody hen, a sick
  // bird) to photograph all ten of them. `import.meta.env.DEV` is false in a
  // production build, so this is not in the shipped bundle.
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).clayFarm = { store, router, shell, game };
  }

  // PWA: the worker is what makes the app installable and what receives push.
  // Registering it is a convenience, never a gate, so it is fire-and-forget and
  // every failure path in there returns quietly. `ensurePushRegistered` then
  // re-sends an endpoint the player already agreed to — no prompt, no permission
  // request, only a device that is already subscribed.
  void registerServiceWorker().then((outcome) => {
    if (outcome === 'registered' || outcome === 'existing') {
      void ensurePushRegistered(new SupabaseClient());
    }
  });

  // A wholesale state swap (a new game from onboarding, an import, a dev
  // override) needs the world resynced as well as the router.
  bus.on('STATE_LOADED', () => game.resync());

  // The season the numbers come from (PLAN-01 D, REFOCUS §11b). Resolved BEFORE
  // the router opens, because onboarding step 02 spends the chick budget: a
  // season landing after the player has bought something would re-price the
  // shop under them mid-session. Capped, so a dead network costs a moment and
  // never a hang — the bundle is already in force and is a fine answer.
  await resolveSeason(store);

  // Onboarding (01–05) only when there is no save to continue. U7 turns this
  // into a "new game" path off the Settings screen.
  // into a "new game" path off the Settings screen.
  const hasSave = state.progression.onboarded === true;
  router.reset(hasSave ? coopScreen() : homePickScreen());
}

void boot().catch((err) => {
  console.error('[app] boot failed', err);
  const message = document.createElement('div');
  message.className = 'cf-app__column';
  message.textContent = `Clay Farm failed to start: ${String(err)}`;
  document.body.replaceChildren(message);
});
